// Version 4 additions: no backend or inferred historical collection membership.
export const round = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
export function sellingPrice(mrp, discount) {
  if (
    mrp == null ||
    mrp === "" ||
    discount == null ||
    discount === "" ||
    !Number.isFinite(Number(mrp)) ||
    !Number.isFinite(Number(discount)) ||
    Number(mrp) < 0 ||
    Number(discount) < 0 ||
    Number(discount) > 100
  )
    return null;
  return round(Number(mrp) * (1 - Number(discount) / 100));
}
function upgrade4(d) {
  if (d.version >= 4) return d;
  const p = d.settings.promotion;
  if (!d.coupons)
    d.coupons = [
      {
        id: "coupon-legacy",
        code: p.code,
        percent: p.percent,
        minimum: p.minimum,
        active: p.active,
        newOnly: p.newOnly,
        stacking: p.stacking,
      },
    ];
  if (!d.couponAliases)
    d.couponAliases =
      p.code.toUpperCase() === "WELCOME15"
        ? []
        : [
            {
              code: "WELCOME15",
              couponId: d.coupons[0].id,
              active: p.welcome15,
            },
          ];
  if (!d.promotions)
    d.promotions = {
      launch: {
        id: "launch",
        name: "Launch discount",
        active: p.launchActive,
        percent: p.launchPercent,
      },
      bundle: {
        id: "bundle",
        name: "Everyday Essentials bundle",
        active: p.bundleActive,
        percent: p.bundlePercent,
        skus: [...p.bundleSkus],
      },
    };
  for (const product of d.products)
    for (const s of product.skus)
      if (s.discount == null) {
        s.discount =
          Number.isFinite(s.mrp) && s.mrp > 0 && Number.isFinite(s.price)
            ? (1 - s.price / s.mrp) * 100
            : 0;
        s.discountBasis =
          "Derived from saved MRP and price; original price retained";
      }
  for (const o of d.orders)
    for (const i of o.items)
      if (!i.collectionId) i.collectionHistory = "Missing legacy snapshot";
  d.dashboardPeriod = d.dashboardPeriod || { mode: "all" };
  d.sidebarCollapsed = d.sidebarCollapsed ?? false;
  d.version = 4;
  return d;
}
export function validatePromotions(d, p) {
  for (const v of Object.values(p)) {
    if (!Number.isFinite(v.percent) || v.percent < 0 || v.percent > 100)
      throw Error("Discounts must be between 0 and 100%.");
  }
  const ids = d.products
    .filter((p) => !p.archived)
    .flatMap((p) => p.skus.map((s) => s.id));
  if (
    p.bundle.skus.some((x) => !ids.includes(x)) ||
    new Set(p.bundle.skus).size !== p.bundle.skus.length
  )
    throw Error("Choose valid, unique bundle SKUs.");
  if (p.bundle.active && p.bundle.skus.length < 3)
    throw Error(
      "Assign at least three eligible SKUs before activating the bundle.",
    );
  return p;
}
export function validateCoupon(d, c, aliases = d.couponAliases) {
  if (!/^[A-Z0-9_-]{2,32}$/i.test(c.code))
    throw Error(
      "Coupon code must contain 2–32 letters, numbers, underscores or hyphens.",
    );
  const code = c.code.toUpperCase();
  if (
    d.coupons.some((x) => x.id !== c.id && x.code.toUpperCase() === code) ||
    aliases.some((a) => a.couponId !== c.id && a.code.toUpperCase() === code)
  )
    throw Error("Coupon codes and aliases must be unique (case insensitive).");
  if (!Number.isFinite(c.percent) || c.percent < 0 || c.percent > 100)
    throw Error("Discount must be between 0 and 100%.");
  if (!Number.isFinite(c.minimum) || c.minimum < 0)
    throw Error("Minimum merchandise value must be nonnegative.");
  validateApplicability(d, c);
  return c;
}
export function discountQuote(d, items, customerId, coupon, offer) {
  const subtotal = items.reduce((n, i) => n + i.price * i.qty, 0);
  let discount = 0,
    applied = "",
    record;
  if (coupon) {
    const code = coupon.trim().toUpperCase(),
      alias = d.couponAliases.find((a) => a.code.toUpperCase() === code);
    record =
      d.coupons.find((c) => c.code.toUpperCase() === code) ||
      d.coupons.find((c) => c.id === alias?.couponId);
    if (!record?.active || (alias && !alias.active))
      throw Error("Coupon is inactive or unknown.");
    if (subtotal < record.minimum)
      throw Error(
        "Coupon requires merchandise value of ₹" + record.minimum + " or more.",
      );
    if (
      record.newOnly &&
      d.orders.some(
        (o) => o.customerId === customerId && o.status !== "Cancelled",
      )
    )
      throw Error("This coupon is for new customers only.");
    const eligible = items
      .filter(
        (i) =>
          record.applicability !== "selected" ||
          ((record.productIds || []).includes(i.productId) &&
            d.products.some(
              (p) =>
                p.id === i.productId && !p.archived && p.status === "Active",
            )),
      )
      .reduce((n, i) => n + i.price * i.qty, 0);
    if (!eligible)
      throw Error("Coupon has no eligible product items in this order.");
    discount = (eligible * record.percent) / 100;
    applied = code;
  }
  if (offer) {
    if (record && !record.stacking) throw Error("Coupon stacking is disabled.");
    throw Error(
      "Legacy launch and bundle offers are retired for future orders.",
    );
  }
  return { discount: round(discount), applied };
}
const validDay = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || "")) return false;
  const d = new Date(v + "T00:00:00Z");
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
export function periodBounds(p) {
  let start,
    end,
    label = "All time";
  if (p.mode === "day") {
    start = end = p.day;
    label = p.day;
  } else if (p.mode === "month") {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(p.month || ""))
      throw Error("Select a valid month and year.");
    start = p.month + "-01";
    const [y, m] = p.month.split("-").map(Number);
    end = p.month + "-" + new Date(Date.UTC(y, m, 0)).getUTCDate();
    label = p.month;
  } else if (p.mode === "year") {
    if (!/^\d{4}$/.test(String(p.year)) || +p.year < 1900 || +p.year > 9999)
      throw Error("Enter a year between 1900 and 9999.");
    start = p.year + "-01-01";
    end = p.year + "-12-31";
    label = String(p.year);
  } else if (p.mode === "range") {
    start = p.start;
    end = p.end;
    label = start + " to " + end;
  } else if (p.mode !== "all") throw Error("Choose a valid period.");
  if (p.mode !== "all" && (!validDay(start) || !validDay(end) || start > end))
    throw Error("Select valid dates with the end on or after the start.");
  return { start, end, label };
}
export function kolkataDay(value) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value || ""))
    return validDay(value) ? value : null;
  const normalized =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value || "")
      ? value + "+05:30"
      : value;
  const date = new Date(normalized);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}
export function dashboardData(d, p = d.dashboardPeriod) {
  const b = periodBounds(p || { mode: "all" }),
    orders = d.orders.filter((o) => {
      const day = kolkataDay(o.date);
      return day && (b.start == null || (day >= b.start && day <= b.end));
    });
  const collections = new Map(
    ["INDRA", "VANYA", "SUTRA", "KALA", "VEDA", "AADIVASI", "BHOOMI"].map(
      (code) => [
        code,
        { id: code, name: code === "VANYA" ? "ARANYA" : code, amount: 0 },
      ],
    ),
  );
  for (const c of d.collections)
    collections.set(c.id, { id: c.id, name: c.name, amount: 0 });
  let revenue = 0,
    merchandise = 0,
    unallocated = 0;
  const products = new Set(),
    warnings = [];
  for (const o of orders) {
    if (o.status === "Cancelled") continue;
    const payments = d.payments.filter((p) => p.orderId === o.id);
    if (
      !payments.some((p) => p.status === "Success") ||
      payments.some((p) => p.status === "Refunded")
    )
      continue;
    const gross = o.items.reduce((n, i) => n + i.qty * i.price, 0),
      net = Math.max(0, gross - (o.discount || 0)),
      shipping = o.shipping || 0,
      total = net + shipping;
    const paid = payments
      .filter((p) => p.status === "Success")
      .reduce((n, p) => n + (Number.isFinite(p.amount) ? p.amount : total), 0);
    const refunds = payments.reduce(
      (n, p) => n + (Number.isFinite(p.refundedAmount) ? p.refundedAmount : 0),
      0,
    );
    const retained = Math.max(0, Math.min(total, paid) - refunds);
    if (!retained) continue;
    revenue += retained;
    const ratio = total ? retained / total : 0,
      netMerch = net * ratio;
    merchandise += netMerch;
    for (const i of o.items) {
      if (i.price * i.qty > 0) products.add(i.productId);
      const amount = gross ? ((i.price * i.qty) / gross) * netMerch : 0;
      if (!i.collectionId) {
        unallocated += amount;
        if (amount) warnings.push(o.id + ": collection history unavailable");
        continue;
      }
      if (!collections.has(i.collectionId))
        collections.set(i.collectionId, {
          id: i.collectionId,
          name: i.collectionName || i.collectionId,
          amount: 0,
        });
      collections.get(i.collectionId).amount += amount;
    }
  }
  if (unallocated > 0)
    collections.set("unallocated", {
      id: "unallocated",
      name: "Unallocated · missing history",
      amount: unallocated,
    });
  return {
    orders,
    revenue: round(revenue),
    productsSold: products.size,
    lowStock: d.products.filter(
      (p) => !p.archived && p.skus.some((s) => s.stock <= s.threshold),
    ).length,
    merchandise: round(merchandise),
    collections: [...collections.values()].map((c) => ({
      ...c,
      amount: round(c.amount),
      percent: merchandise ? (c.amount / merchandise) * 100 : 0,
    })),
    warnings: [...new Set(warnings)],
    label: b.label,
  };
}
export function customerSummary(d, c) {
  const orders = d.orders.filter((o) => o.customerId === c.id);
  return {
    orders,
    count: orders.length,
    total: round(
      orders
        .filter((o) => o.status !== "Cancelled")
        .reduce(
          (n, o) =>
            n +
            o.items.reduce((n, i) => n + i.price * i.qty, 0) -
            (o.discount || 0) +
            (o.shipping || 0),
          0,
        ),
    ),
  };
}

export const customerExamples = {
  c1: {
    phone: "9000000001",
    address: "Example residence 1, Sample Lane, Pune",
    status: "Active",
    signupDate: "2026-09-20",
  },
  c2: {
    phone: "9000000002",
    address: "Example residence 2, Sample Lane, Mumbai",
    status: "Active",
    signupDate: "2026-09-21",
  },
  c3: {
    phone: "9000000003",
    address: "Example residence 3, Sample Lane, Bengaluru",
    status: "Pending",
    signupDate: "2026-09-22",
  },
};
export function upgrade(d) {
  d = upgrade4(d);
  if (d.version >= 5) return d;
  for (const c of d.coupons) {
    c.applicability = c.applicability || "all";
    c.productIds = c.productIds || [];
  }
  const names = { c1: "Meera Kulkarni", c2: "Arjun Shah", c3: "Nisha Rao" };
  for (const c of d.customers)
    if (
      c.exampleRecord &&
      c.name === names[c.id] &&
      c.email?.endsWith("@example.com")
    ) {
      for (const [k, v] of Object.entries(customerExamples[c.id] || {}))
        if (
          c[k] === undefined &&
          !(k === "status" && c.accountStatus !== undefined) &&
          !(
            k === "signupDate" &&
            (c.createdAt !== undefined || c.registrationDate !== undefined)
          )
        )
          c[k] = v;
    }
  d.legacyOffersRetired = true;
  d.version = 5;
  return d;
}
export function validateApplicability(d, c) {
  if (!["all", "selected"].includes(c.applicability || "all"))
    throw Error("Choose All products or Selected products.");
  if (c.applicability === "selected") {
    if (!Array.isArray(c.productIds) || !c.productIds.length)
      throw Error("Select at least one applicable product.");
    const previous = d.coupons.find((x) => x.id === c.id)?.productIds || [];
    for (const pid of c.productIds)
      if (
        !d.products.some(
          (p) => p.id === pid && !p.archived && p.status === "Active",
        ) &&
        !previous.includes(pid)
      )
        throw Error("Choose active, non-archived products.");
    if (
      c.active &&
      !c.productIds.some((pid) =>
        d.products.some(
          (p) => p.id === pid && !p.archived && p.status === "Active",
        ),
      )
    )
      throw Error(
        "An active selected-products coupon needs at least one available product; choose a product or deactivate this coupon.",
      );
  }
  return c;
}
export function customerRecord(d, c) {
  const a = customerSummary(d, c);
  return {
    ...c,
    address:
      typeof c.address === "object" && c.address
        ? Object.values(c.address).filter(Boolean).join(", ")
        : c.address || "",
    status: c.status ?? c.accountStatus ?? "",
    registration: c.signupDate ?? c.createdAt ?? c.registrationDate ?? "",
    orderCount: a.count,
    orderValue: a.total,
    orders: a.orders,
  };
}
export function validateCustomer(d, c) {
  if (!c.name?.trim()) throw Error("Customer name is required.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email || ""))
    throw Error("Enter a valid customer email.");
  if (
    d.customers.some(
      (x) => x.id !== c.id && x.email?.toLowerCase() === c.email.toLowerCase(),
    )
  )
    throw Error("Customer email is already in use.");
  if (c.phone && !/^\+?[0-9 ()-]{7,18}$/.test(c.phone))
    throw Error("Enter a valid phone number or leave it blank.");
  if (c.signupDate && !validDay(c.signupDate))
    throw Error("Enter a valid registration date.");
  return c;
}
